/**
 * WalletSummaryCard — F-012 Phase 3 (IMPL-P3-006).
 *
 * Read-only summary of a business account's wallet: saldo, fido attuale,
 * disponibilità effettiva, ultima ricarica and wallet state. When the wallet is
 * blocked, a destructive Alert banner sits at the top of the card.
 *
 * WCAG: state is conveyed via label + icon (WalletStateBadge), never color
 * alone.
 */
import { ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import type { ClienteDetailView } from '../lib/admin-client';
import { centsToEuro, formatDateTime, tipologiaLabel } from '../lib/format';
import { AvailabilityBadge, WalletStateBadge } from './WalletBadges';

interface WalletSummaryCardProps {
  detail: ClienteDetailView;
  /** Last top-up timestamp (ISO). Optional — not present in the detail payload. */
  lastTopUpAt?: string | null;
}

export function WalletSummaryCard({ detail, lastTopUpAt }: WalletSummaryCardProps) {
  const { account, balanceCents, availableCents } = detail;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle>{account.name ?? account.email}</CardTitle>
            <p className="text-sm text-muted-foreground">{account.email}</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Badge variant="outline">{tipologiaLabel(account.tipologia)}</Badge>
            <WalletStateBadge
              walletBlocked={account.walletBlocked}
              gracePeriodUntil={account.gracePeriodUntil}
            />
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {account.walletBlocked ? (
          <Alert variant="destructive">
            <ShieldAlert className="h-4 w-4" aria-hidden="true" />
            <AlertTitle>Wallet bloccato</AlertTitle>
            <AlertDescription>
              Questo utente non può registrare nuove pratiche finché il wallet resta bloccato.
            </AlertDescription>
          </Alert>
        ) : null}

        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">Saldo</dt>
            <dd className="mt-1 text-lg font-semibold tabular-nums">{centsToEuro(balanceCents)}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">Fido attuale</dt>
            <dd className="mt-1 text-lg font-semibold tabular-nums">
              {centsToEuro(account.fidoLimitCents)}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">
              Disponibilità
            </dt>
            <dd className="mt-1 flex items-center gap-2">
              <span className="text-lg font-semibold tabular-nums">
                {centsToEuro(availableCents)}
              </span>
              <AvailabilityBadge availableCents={availableCents} />
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">
              Ultima ricarica
            </dt>
            <dd className="mt-1 text-sm">
              {lastTopUpAt ? formatDateTime(lastTopUpAt) : '—'}
            </dd>
          </div>
        </dl>

        {account.gracePeriodUntil && !account.walletBlocked ? (
          <p className="text-xs text-amber-700 dark:text-amber-300">
            Grace period attivo fino al {formatDateTime(account.gracePeriodUntil)}.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
